import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { 
  FaUsers, FaNewspaper, FaVideo, FaRobot, FaEye, 
  FaClock, FaFire 
} from 'react-icons/fa';

const BulletinActivityTab = () => {
  const [publicCasefiles, setPublicCasefiles] = useState([]);
  const [allEvidence, setAllEvidence] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch public casefiles
  useEffect(() => {
    const q = query(
      collection(db, 'casefiles'),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const files = [];
      querySnapshot.forEach((doc) => {
        files.push({ id: doc.id, ...doc.data() });
      });
      setPublicCasefiles(files);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch all evidence for public casefiles
  useEffect(() => {
    if (publicCasefiles.length === 0) return;

    const casefileIds = publicCasefiles.map(cf => cf.id);
    // Temporary: Use simple collection query while indexes are building
    const q = query(
      collection(db, 'evidence'),
      where('casefileId', 'in', casefileIds)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const evidence = [];
      querySnapshot.forEach((doc) => {
        evidence.push({ id: doc.id, ...doc.data() });
      });
      setAllEvidence(evidence);
    });

    return () => unsubscribe();
  }, [publicCasefiles]);

  // Calculate community statistics
  const communityStats = useMemo(() => {
    const totalPublicCases = publicCasefiles.length;
    const totalEvidence = allEvidence.length;
    
    // Calculate unique contributors
    const contributors = new Set();
    publicCasefiles.forEach(casefile => {
      if (casefile.creatorEmail) contributors.add(casefile.creatorEmail);
    });
    
    // Calculate AI analysis stats
    let analyzedEvidence = 0;
    let totalVehiclesDetected = 0;
    let totalPlatesDetected = 0;
    
    allEvidence.forEach(evidence => {
      if (evidence.analysis && evidence.analysis.trim() !== '') {
        analyzedEvidence++;
        
        try {
          const analysisText = evidence.analysis;
          if (!analysisText.toLowerCase().includes('error:') && 
              analysisText.trim().toLowerCase() !== 'n/a') {
            
            const jsonMatch = analysisText.match(/\[.*\]|\{.*\}/s);
            if (jsonMatch) {
              const jsonData = JSON.parse(jsonMatch[0]);
              if (Array.isArray(jsonData)) {
                totalVehiclesDetected += jsonData.length;
                
                const platesInEvidence = jsonData.filter(item => 
                  item["License Plate Number"] && 
                  item["License Plate Number"] !== 'Not detected' &&
                  item["License Plate Number"].length >= 2
                ).length;
                totalPlatesDetected += platesInEvidence;
              }
            }
          }
        } catch (e) {
          // Fallback parsing if needed
        }
      }
    });

    // Calculate recent activity (last 7 days)
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const recentCases = publicCasefiles.filter(file => 
      file.createdAt && file.createdAt.toDate() > weekAgo
    ).length;
    
    const recentEvidence = allEvidence.filter(evidence => 
      evidence.createdAt && evidence.createdAt.toDate() > weekAgo
    ).length;

    return {
      totalPublicCases,
      totalEvidence,
      uniqueContributors: contributors.size,
      analyzedEvidence,
      totalVehiclesDetected,
      totalPlatesDetected,
      recentCases,
      recentEvidence,
      analysisRate: totalEvidence > 0 ? Math.round((analyzedEvidence / totalEvidence) * 100) : 0
    };
  }, [publicCasefiles, allEvidence]);

  // Generate recent activity feed
  const activityFeed = useMemo(() => {
    const activities = [];
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Recent casefile creations
    publicCasefiles
      .filter(file => file.createdAt && file.createdAt.toDate() > thirtyDaysAgo)
      .sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate())
      .slice(0, 5)
      .forEach(file => {
        activities.push({
          type: 'casefile',
          title: `New public case: ${file.title}`,
          subtitle: `by ${file.creatorEmail || 'Anonymous'}`,
          date: file.createdAt.toDate(),
          link: `/casefile/${file.id}`,
          icon: FaNewspaper,
          color: '#E53935'
        });
      });

    // Recent evidence uploads
    allEvidence
      .filter(evidence => evidence.createdAt && evidence.createdAt.toDate() > thirtyDaysAgo)
      .sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate())
      .slice(0, 5)
      .forEach(evidence => {
        const casefile = publicCasefiles.find(c => c.id === evidence.casefileId);
        if (casefile) {
          activities.push({
            type: 'evidence',
            title: `Evidence uploaded: ${evidence.fileName}`,
            subtitle: `to ${casefile.title}`,
            date: evidence.createdAt.toDate(),
            link: `/casefile/${evidence.casefileId}`,
            icon: FaVideo,
            color: '#4CAF50'
          });
        }
      });

    return activities
      .sort((a, b) => b.date - a.date)
      .slice(0, 8);
  }, [publicCasefiles, allEvidence]);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="tab-content bulletin-activity-tab">
        <div className="tab-header">
          <h2>Community Activity</h2>
        </div>
        <p>Loading community data...</p>
      </div>
    );
  }

  return (
    <div className="tab-content bulletin-activity-tab">
      <div className="tab-header">
        <h2>Community Activity & Stats</h2>
        <p>Real-time community intelligence and activity</p>
      </div>

      {/* Community Statistics Grid */}
      <div className="stats-grid">
        <div className="stat-box community-stat">
          <FaUsers className="stat-icon" />
          <div className="stat-content">
            <h3>{communityStats.uniqueContributors}</h3>
            <p>Community Members</p>
            <span className="stat-detail">Active contributors</span>
          </div>
        </div>

        <div className="stat-box community-stat">
          <FaNewspaper className="stat-icon" />
          <div className="stat-content">
            <h3>{communityStats.totalPublicCases}</h3>
            <p>Public Cases</p>
            <span className="stat-detail">{communityStats.recentCases} this week</span>
          </div>
        </div>

        <div className="stat-box community-stat">
          <FaVideo className="stat-icon" />
          <div className="stat-content">
            <h3>{communityStats.totalEvidence}</h3>
            <p>Evidence Files</p>
            <span className="stat-detail">{communityStats.recentEvidence} this week</span>
          </div>
        </div>

        <div className="stat-box community-stat">
          <FaRobot className="stat-icon" />
          <div className="stat-content">
            <h3>{communityStats.analysisRate}%</h3>
            <p>AI Analysis Rate</p>
            <span className="stat-detail">{communityStats.analyzedEvidence} processed</span>
          </div>
        </div>

        <div className="stat-box community-stat">
          <FaEye className="stat-icon" />
          <div className="stat-content">
            <h3>{communityStats.totalVehiclesDetected}</h3>
            <p>Vehicles Detected</p>
            <span className="stat-detail">{communityStats.totalPlatesDetected} plates found</span>
          </div>
        </div>

        <div className="stat-box community-stat">
          <FaFire className="stat-icon" />
          <div className="stat-content">
            <h3>{communityStats.recentCases + communityStats.recentEvidence}</h3>
            <p>Weekly Activity</p>
            <span className="stat-detail">New cases & evidence</span>
          </div>
        </div>
      </div>

      {/* Community Insights */}
      <div className="insights-section">
        <h3>🌐 Community Insights</h3>
        <div className="insights-content">
          {communityStats.totalPublicCases >= 10 && (
            <p>🔥 Strong community! {communityStats.totalPublicCases} public cases are available for collaboration.</p>
          )}
          {communityStats.analysisRate >= 75 && (
            <p>🤖 Excellent AI coverage! {communityStats.analysisRate}% of evidence has been processed by AI.</p>
          )}
          {communityStats.totalPlatesDetected >= 5 && (
            <p>🚗 Great detective work! {communityStats.totalPlatesDetected} license plates have been identified.</p>
          )}
          {communityStats.recentCases >= 3 && (
            <p>📈 Active community! {communityStats.recentCases} new cases shared this week.</p>
          )}
          {communityStats.uniqueContributors >= 5 && (
            <p>👥 Diverse community! {communityStats.uniqueContributors} different contributors sharing intelligence.</p>
          )}
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="activity-section">
        <h3>🕒 Recent Community Activity</h3>
        {activityFeed.length === 0 ? (
          <div className="empty-activity">
            <p>No recent community activity to show.</p>
            <p>Be the first to share a public case!</p>
          </div>
        ) : (
          <ul className="activity-feed">
            {activityFeed.map((activity, index) => {
              const IconComponent = activity.icon;
              return (
                <li key={index} className="activity-item">
                  <div className="activity-icon" style={{ color: activity.color }}>
                    <IconComponent />
                  </div>
                  <div className="activity-content">
                    <Link to={activity.link} className="activity-title">
                      {activity.title}
                    </Link>
                    {activity.subtitle && (
                      <p className="activity-subtitle">{activity.subtitle}</p>
                    )}
                    <span className="activity-time">
                      <FaClock /> {formatTimeAgo(activity.date)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Community Call to Action */}
      <div className="cta-section">
        <h3>🚀 Join the Community</h3>
        <div className="cta-content">
          <p>Help solve cases and contribute to the community intelligence network!</p>
          <div className="cta-actions">
            <Link to="/dashboard" className="cta-button primary">
              <FaNewspaper /> Create Public Case
            </Link>
            <Link to="/public" className="cta-button secondary">
              <FaEye /> Browse Cases
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulletinActivityTab; 